<script setup lang="ts">
import type { RGB, RGBA } from '@@/bits/post_color'
import { postApprovalColorRgb, postColorRgb } from '@@/bits/post_color'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSurfaceStore } from '@@/pinia/surface'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    color?: 'red' | 'orange' | 'green' | 'blue' | 'purple' | 'primary_blur' | null
    shouldMatchApprovalColor?: boolean
    isPulsating?: boolean
  }>(),
  {
    color: null,
    shouldMatchApprovalColor: false,
    isPulsating: false,
  },
)

const surfaceStore = useSurfaceStore()
const darkModeStore = useDarkModeStore()
const { colorScheme } = storeToRefs(surfaceStore)
const { isDarkMode } = storeToRefs(darkModeStore)

const isLightColorScheme = computed<boolean>(() => colorScheme.value === 'light')

const tipColor = computed<string>(() => {
  // some times we force the tip to be white, e.g. for map format pin drop suggestion
  if (props.color === 'primary_blur') {
    return isDarkMode.value ? 'rgba(0, 0, 0, 0.86)' : 'rgba(255, 255, 255, 0.86)'
  }
  if (props.shouldMatchApprovalColor) {
    return parseColorToRgbString(
      postApprovalColorRgb({
        postColor: props.color,
        isLightColorScheme: isLightColorScheme.value,
      }),
    )
  }
  return parseColorToRgbString(
    postColorRgb({
      postColor: props.color,
      isLightColorScheme: isLightColorScheme.value,
    }),
  )
})

const parseColorToRgbString = (color: RGB | RGBA): string => `rgb(${color.r}, ${color.g}, ${color.b})`
</script>

<template>
  <div :class="['color-' + (color || 'default'), 'drop-shadow-elevation-1']">
    <!-- slot allows substituting any child needing the popup arrow tip style.-->
    <slot />

    <!-- The tip -->
    <svg
      class="popup-bubble-anchor-tip"
      :class="{ 'is-animated': isPulsating, pulsate: isPulsating }"
      width="55"
      height="16"
      viewBox="0 0 55 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M0.00219655 0C6.36739 0 12.4719 2.52856 16.9727 7.02944L24.1939 14.2506C25.756 15.8127 28.2887 15.8127 29.8508 14.2506L37.072 7.02943C41.5729 2.52856 47.6774 -1.32551e-06 54.0426 0C54.0996 1.18735e-08 -0.398293 0 0.00219655 0Z"
        :fill="tipColor"
      />
    </svg>
  </div>
</template>

<style lang="scss">
@import '@assets/stylesheets/3/modules/all';
/* This element draws the tip. */
.popup-bubble-anchor-tip {
  position: absolute;
  left: 50%;
  /* Center the tip horizontally. */
  transform: translateX(-50%);
}

.popup-bubble-anchor-tip-black {
  display: block;
}

.color-default {
  .popup-bubble-anchor-tip-black {
    [data-color-scheme='light'] & {
      display: block;
    }
    [data-color-scheme='dark'] & {
      display: none;
    }
  }
  .popup-bubble-anchor-tip-white {
    [data-color-scheme='dark'] & {
      display: block;
    }
  }
}
.popup-content .wish .real-wish {
  @include shadow-z2;
}
</style>
